import { skipToken, useQuery } from '@tanstack/react-query';
import { cdpSourceOptionsApi } from './client';
import { cdpSourceOptionKeys, type AvailableSource } from './entity';

// Both reads are the non-suspending `…Optional` form: their callers mount
// before the workspace or option settles. While an id is null the read parks on
// a sentinel key with `skipToken` (see `useDomainsOptional`).

const STALE_TIME = 60 * 1000;

const withoutHidden = (options: AvailableSource[]) => options.filter((option) => !option.hidden);

/**
 * The workspace's source catalog. One cached list serves both views:
 * `includeHidden: false` filters the hidden entries out when it reads.
 */
export const useSourceOptionsOptional = ({
  workspaceId,
  includeHidden = true,
  enabled = true,
}: {
  workspaceId: number | null;
  includeHidden?: boolean;
  enabled?: boolean;
}) => {
  const active = workspaceId != null && enabled;
  return useQuery({
    queryKey: active ? cdpSourceOptionKeys.lists(workspaceId) : cdpSourceOptionKeys.inactive('list'),
    queryFn: active ? () => cdpSourceOptionsApi.list(workspaceId) : skipToken,
    staleTime: STALE_TIME,
    select: includeHidden ? undefined : withoutHidden,
  });
};

export const useSourceOptionManifestOptional = ({
  workspaceId,
  optionId,
}: {
  workspaceId: number | null;
  optionId: string | null | undefined;
}) => {
  const active = workspaceId != null && optionId != null;
  return useQuery({
    queryKey: active ? cdpSourceOptionKeys.manifest(workspaceId, optionId) : cdpSourceOptionKeys.inactive('manifest'),
    queryFn: active ? () => cdpSourceOptionsApi.manifest(workspaceId, optionId) : skipToken,
    staleTime: STALE_TIME,
  });
};
